import { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import ActionButton from "../../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../../dao/ElementaryProgressReportDao";
import SetupStudentSelection from "./SetupStudentSelection";
import SetupExcludeItems from "./SetupExcludeItems";
import SetupAddDiffItems from "./SetupAddDiffItems";

import { GlobalContext } from "../../contextProvider/ContextProvider";

import "bootstrap/dist/css/bootstrap.min.css";
import "../../../styles/SetupDifferentiation.scss";

/**
 * SetupDifferentiationModal - Multi-step modal for setting up differentiation
 * Step 1: Select students
 * Step 2: Exclude template items
 * Step 3: Add differentiated items from higher grades
 */
const SetupDifferentiationModal = ({
    show,
    onHide,
    allStudents,
    studentsWithDiff,
    selectedTemplate,
    selectedGrade,
    onComplete
}) => {
    const { state: globalState } = useContext(GlobalContext);
    const { token, userDetails } = globalState || {};

    const [step, setStep] = useState(1);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [excludedItemIds, setExcludedItemIds] = useState([]);
    const [addedItems, setAddedItems] = useState([]);
    const [saving, setSaving] = useState(false);

    const handleClose = () => {
        setStep(1);
        setSelectedStudentIds([]);
        setExcludedItemIds([]);
        setAddedItems([]);
        onHide();
    };

    const handleNext = async () => {
        if (step === 2) {
            await saveExclusions();
        }
        if (step < 3) {
            setStep(step + 1);
        }
    };

    const handleBack = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const handleComplete = async () => {
        await saveDiffItems();
        onComplete({
            gradingProfileStudentIds: selectedStudentIds,
            addedItems
        });
        handleClose();
    };

    const saveExclusions = async () => {
        if (excludedItemIds.length === 0) return;

        setSaving(true);
        try {
            const excludeContentRatingAssignments = {};

            selectedStudentIds.forEach((studentId) => {
                const student = allStudents.find((s) => s.key === studentId);
                if (!student?.sourceProfile) return;

                const profile = student.sourceProfile;
                const gradingCriteria = profile.gradingCriteriaList?.find(
                    (criteria) => Number.parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
                );

                if (!gradingCriteria) return;

                const groupKey = gradingCriteria.key;
                if (!excludeContentRatingAssignments[groupKey]) {
                    excludeContentRatingAssignments[groupKey] = {
                        gradingProfileStudentIds: [],
                        gradingCriteria: Number(gradingCriteria.key)
                    };
                }
                excludeContentRatingAssignments[groupKey].gradingProfileStudentIds.push(studentId);
            });

            const multiExclusionsData = {
                excludeContentRatingAssignments: Object.values(excludeContentRatingAssignments),
                lastRatedGuid: userDetails?.uid,
                excludeContents: excludedItemIds.map((item) => ({
                    templateContentId: item.templateContentId,
                    contentType: item.contentType
                }))
            };

            await ElementaryProgressReportDao({
                action: "updateMultiStudentExclusions",
                data: multiExclusionsData,
                token
            });

            toast.success("Exclusions saved successfully.");
        } catch (error) {
            console.error("Failed to save exclusions:", error);
            toast.error("Failed to save exclusions. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const saveDiffItems = async () => {
        if (addedItems.length === 0) return;

        setSaving(true);
        try {
            const studentDiffAssignments = {};

            selectedStudentIds.forEach((studentId) => {
                const student = allStudents.find((s) => s.key === studentId);
                if (!student?.sourceProfile) return;

                const profile = student.sourceProfile;
                const gradingCriteria = profile.gradingCriteriaList?.find(
                    (criteria) => Number.parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
                );

                if (!gradingCriteria) return;

                const groupKey = `${gradingCriteria.key}_${profile.ownerGuid}`;
                if (!studentDiffAssignments[groupKey]) {
                    studentDiffAssignments[groupKey] = {
                        gradingProfileStudentIds: [],
                        gradingCriteria: Number(gradingCriteria.key),
                        ownerGuid: profile.ownerGuid
                    };
                }
                studentDiffAssignments[groupKey].gradingProfileStudentIds.push(studentId);
            });

            const data = {
                studentDiffAssignments: Object.values(studentDiffAssignments),
                lastRaterGuid: userDetails?.uid,
                includeDiffRatings: addedItems.map((item) => ({
                    gradeToContentId: item.gradeToContentId,
                    contentId: item.contentId,
                    contentType: item.contentType
                }))
            };

            await ElementaryProgressReportDao({
                action: "createMultiStudentDiff",
                data,
                token
            });

            toast.success("Differentiated items saved successfully.");
        } catch (error) {
            console.error("Failed to save diff items:", error);
            toast.error("Failed to save differentiated items. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
            <Modal.Header>
                <Modal.Title>Set Up Differentiation - Step {step} of 3</Modal.Title>
            </Modal.Header>
            {step > 1 && selectedStudentIds.length > 0 && (
                <>
                    <div className="setup-selected-students">
                        <span className="selected-student-label">Selected Students ({selectedStudentIds.length}):</span>{" "}
                        {selectedStudentIds
                            .map((id) => {
                                const student = allStudents.find((s) => s.key === id);
                                return student ? `${student.firstName} ${student.lastName}` : null;
                            })
                            .filter(Boolean)
                            .join(", ")}
                    </div>
                    <hr className="setup-selected-students-divider" />
                </>
            )}
            <Modal.Body>
                {step === 1 && (
                    <SetupStudentSelection
                        allStudents={allStudents}
                        studentsWithDiff={studentsWithDiff}
                        selectedStudentIds={selectedStudentIds}
                        setSelectedStudentIds={setSelectedStudentIds}
                    />
                )}
                {step === 2 && (
                    <SetupExcludeItems
                        selectedTemplate={selectedTemplate}
                        excludedItemIds={excludedItemIds}
                        setExcludedItemIds={setExcludedItemIds}
                    />
                )}
                {step === 3 && (
                    <>
                        <SetupAddDiffItems
                            selectedTemplate={selectedTemplate}
                            selectedGrade={selectedGrade}
                            addedItems={addedItems}
                            setAddedItems={setAddedItems}
                        />
                        {excludedItemIds.length > 0 && (
                            <div className="setup-warning-message">
                                <i className="bi bi-exclamation-triangle-fill" />
                                <span>
                                    Please note, closing the setup process before adding differentiated content for the
                                    selected student(s) will result in the loss of the ability to edit multiple students
                                    at once. You will still be able to individually add differentiated content and/or
                                    can reset for one or more students and begin setup again.
                                </span>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <ActionButton
                    className="action-button-cancel"
                    label={excludedItemIds.length > 0 && step === 3 ? "Close" : "Cancel"}
                    onClick={handleClose}
                />
                {step > 1 && step < 3 && <ActionButton label="Back" onClick={handleBack} />}
                {step < 3 && (
                    <ActionButton
                        label={step === 2 && excludedItemIds.length > 0 ? "Save and Next" : "Next"}
                        onClick={handleNext}
                        disable={(step === 1 && selectedStudentIds.length === 0) || saving}
                    />
                )}
                {step === 3 && <ActionButton label="Save" onClick={handleComplete} disable={saving} />}
            </Modal.Footer>
        </Modal>
    );
};

SetupDifferentiationModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    allStudents: PropTypes.array.isRequired,
    studentsWithDiff: PropTypes.array.isRequired,
    selectedTemplate: PropTypes.object,
    selectedGrade: PropTypes.string,
    onComplete: PropTypes.func.isRequired
};

export default SetupDifferentiationModal;
